// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// =============================================================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useI18n } from '../i18n';
import { LayoutDashboard, MailPlus, ShieldOff, ShieldCheck, Pencil, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLogoLicence } from '../lib/logoLicence';
import {
  getLicenseStats, exportLicenseConsommations, exportLicenseResultatsAnalyses,
  getLicenseOrgUnits, getLicenseAnnotations, getLicenseRatings,
} from '../services/api';
import SortableFilterableTable from '../components/SortableFilterableTable';
import ConsommationsClient from '../components/ConsommationsClient';
// `NIVEAUX_ORG`, `ordonnerArbre` et la modale vivent désormais dans le
// composant partagé : l'écran admin d'Evalens gère les mêmes échelons, et une
// copie locale aurait fini par diverger.
import OrganisationEchelons, {
  ordonnerArbre, optionsEchelons, SelecteurEchelon,
} from '../components/OrganisationEchelons';

const selectStyle = { padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 };

/**
 * Filtre à deux étages : l'échelon d'abord (à gauche), le membre ensuite.
 *
 * Sur une structure à 250 diagnostiqueurs, une liste de membres non filtrée est
 * illisible — c'est tout l'objet de la cascade.
 *
 * ⚠️ La liste des membres ne propose que les rattachés **DIRECTS** de l'échelon
 * choisi : sélectionner une région fait apparaître son responsable régional,
 * pas les diagnostiqueurs de ses agences — pour ceux-là on descend d'un cran.
 * Le filtre par échelon, lui, porte bien sur tout le sous-arbre côté serveur :
 * ce sont deux questions différentes, « qui est rattaché ici » et « que s'est-il
 * passé sous cet échelon ».
 */
function FiltresOrgMembre({ orgUnits, users, orgUnitId, userId, onChange, t, avecMembre = true }) {
  const membresProposes = orgUnitId ? users.filter(u => u.org_unit_id === orgUnitId) : users;

  const changerEchelon = (valeur) => {
    // Le membre déjà choisi peut ne plus appartenir au nouvel échelon : on le
    // relâche, plutôt que de laisser un filtre actif absent de la liste.
    const membreTientToujours = !userId || !valeur
      || users.some(u => u.id === userId && u.org_unit_id === valeur);
    onChange({ orgUnitId: valeur, userId: membreTientToujours ? userId : '' });
  };

  return (
    <>
      {orgUnits.length > 0 && (
        <select value={orgUnitId} onChange={e => changerEchelon(e.target.value)} style={selectStyle}>
          <option value="">{t('portal_stats_all_org')}</option>
          {optionsEchelons(orgUnits, t)}
        </select>
      )}
      {avecMembre && (
        <select value={userId} onChange={e => onChange({ orgUnitId, userId: e.target.value })}
          title={orgUnitId ? t('portal_members_direct_hint') : undefined} style={selectStyle}>
          <option value="">{t('portal_stats_all_members')}</option>
          {membresProposes.map(u => <option key={u.id} value={u.id}>{u.email}</option>)}
        </select>
      )}
    </>
  );
}

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
// ⛔ Plus de jeton (chantier ⑯) : la session part en cookie, via lib/session.js.
const auth = () => ({});

const ANN_STATUS_COLORS = {
  open:       { bg: '#eff6ff', text: '#1d4ed8' },
  processing: { bg: '#fef3c7', text: '#92400e' },
  rejected:   { bg: '#fee2e2', text: '#991b1b' },
  closed:     { bg: '#f0fdf4', text: '#166534' },
};
const RES_STATUS_COLORS = {
  resolved:    { bg: '#f0fdf4', text: '#166534' },
  unresolved:  { bg: '#fee2e2', text: '#991b1b' },
  in_progress: { bg: '#fef3c7', text: '#92400e' },
  unknown:     { bg: '#f3f4f6', text: '#374151' },
};
const TYPE_COLORS = {
  parsing: { bg: '#fef3c7', text: '#92400e' },
  display: { bg: '#ede9fe', text: '#5b21b6' },
  error:   { bg: '#fee2e2', text: '#991b1b' },
  anomaly: { bg: '#fce7f3', text: '#9d174d' },
  other:   { bg: '#f3f4f6', text: '#374151' },
};
const getAnnotationTypes = (t) => ({
  parsing: t('ann_type_parsing'),
  display: t('ann_type_display'),
  error:   t('ann_type_error'),
  anomaly: t('ann_type_anomaly'),
  other:   t('ann_type_other'),
});
const getAnnotationTargets = (t) => ({
  json_pass1: t('ann_target_json1'),
  json_final: t('ann_target_json2'),
  html:       t('ann_target_html'),
  pdf:        t('ann_target_pdf'),
  general:    t('ann_target_general'),
});

function StatCard({ label, value, sub }) {
  return (
    <div style={{ padding: '16px 20px', background: 'var(--bg-input)', borderRadius: 10, border: '1px solid var(--gray)', minWidth: 140 }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--blue)' }}>{value ?? '—'}</div>
      <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/**
 * Identité d'un Cabinet — nom et logo, réglés par son SEUL titulaire.
 *
 * Le nom et le logo s'affichent chez tous les sièges (barre latérale) et dans
 * les invitations. Une licence Entreprise n'a pas cette section : son identité
 * est posée par Evalens. Le serveur le vérifie de son côté
 * (`_require_titulaire_cabinet`) — masquer la section n'est pas la protection.
 */
function IdentiteCabinet({ lic, onChange }) {
  const { t } = useI18n();
  const logo = useLogoLicence(lic.logo_path);
  const [nom, setNom] = useState(lic.nom || '');
  const [fichier, setFichier] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState({ texte: '', erreur: false });

  const appeler = async (url, options, succes) => {
    setEnvoi(true);
    setMessage({ texte: '', erreur: false });
    try {
      const r = await fetch(url, { ...options, headers: { ...auth(), ...(options.headers || {}) } });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setMessage({ texte: d.error || t('err_generic'), erreur: true }); return; }
      setMessage({ texte: succes, erreur: false });
      setFichier(null);
      await onChange();
    } catch (_) {
      setMessage({ texte: t('err_network'), erreur: true });
    } finally {
      setEnvoi(false);
    }
  };

  const renommer = () => appeler(`${API}/api/license/identite`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ nom }),
  }, t('cabinet_identite_enregistree'));

  const televerser = () => {
    const fd = new FormData();
    fd.append('logo', fichier);
    appeler(`${API}/api/license/logo`, { method: 'POST', body: fd }, t('cabinet_identite_enregistree'));
  };

  const supprimer = () => appeler(`${API}/api/license/logo`, { method: 'DELETE' }, t('cabinet_identite_enregistree'));

  const champ = { padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)' };
  const bouton = { padding: '7px 12px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 12 };

  return (
    <div style={{ border: '1px solid var(--gray)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{t('cabinet_identite_titre')}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>{t('cabinet_identite_texte')}</div>

      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('cabinet_nom_label')}</label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <input type="text" value={nom} maxLength={80} onChange={e => setNom(e.target.value)}
          style={{ ...champ, flex: 1, minWidth: 200 }} />
        <button onClick={renommer} disabled={envoi || nom.trim().length < 2 || nom.trim() === lic.nom}
          style={{ ...bouton, background: 'var(--blue)', color: 'white', border: 'none', fontWeight: 600 }}>
          {t('save')}
        </button>
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>Logo</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {logo && <img src={logo} alt="" style={{ maxHeight: 40, maxWidth: 160, objectFit: 'contain', borderRadius: 4, border: '1px solid var(--gray)' }} />}
        {lic.logo_path && <button onClick={supprimer} disabled={envoi} style={bouton}>{t('ent_delete_logo')}</button>}
        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={e => setFichier(e.target.files[0] || null)} style={{ fontSize: 12 }} />
        {fichier && <button onClick={televerser} disabled={envoi} style={bouton}>{envoi ? '…' : t('ent_upload_logo')}</button>}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{t('ent_logo_hint')}</div>
      {message.texte && (
        <div style={{ fontSize: 12, marginTop: 10, color: message.erreur ? '#ef4444' : '#16a34a' }}>{message.texte}</div>
      )}
    </div>
  );
}

function InviteModal({ licenseId, orgUnits, cabinet, onClose, onSent }) {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  // Rattachement décidé DÈS l'invitation : sans lui, l'invité arrive hors
  // organisation et il faut le ranger à la main après son acceptation.
  const [orgUnitId, setOrgUnitId] = useState('');
  const [rights, setRights] = useState({
    lic_ocr_enabled: false, lic_projects_enabled: false,
    lic_annotations_enabled: false, lic_ratings_enabled: false,
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    setError('');
    setSending(true);
    try {
      const r = await fetch(`${API}/api/enterprise/licenses/${licenseId}/invitations`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, org_unit_id: orgUnitId || null, ...(cabinet ? {} : rights) }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || t('err_generic')); setSending(false); return; }
      onSent();
      onClose();
    } catch (e) { setError(t('err_network')); setSending(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 12, padding: 24, width: 400, maxWidth: '90vw', boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{t('portal_invite_btn')}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        {error && <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid #ef4444', borderRadius: 6, padding: '8px 12px', color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_invite_email_label')}</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
        </div>
        {(orgUnits || []).length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('portal_seat_org')}</label>
            <SelecteurEchelon orgUnits={orgUnits} value={orgUnitId} onChange={setOrgUnitId}
              style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)' }} />
          </div>
        )}
        {cabinet ? (
          <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-muted)' }}>{t('portal_cabinet_rights_included')}</div>
        ) : (
          <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {[['lic_ocr_enabled', t('ent_rights_ocr')], ['lic_projects_enabled', t('ent_rights_projects')],
              ['lic_annotations_enabled', t('ent_rights_annotations')], ['lic_ratings_enabled', t('ent_rights_ratings')]].map(([k, label]) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={rights[k]} onChange={e => setRights(r => ({ ...r, [k]: e.target.checked }))} />{label}
              </label>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>{t('cancel')}</button>
          <button onClick={send} disabled={sending || !email.trim()}
            style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {sending ? '…' : t('portal_invite_btn')}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditSeatModal({ user, orgUnits, cabinet, onSave, onClose }) {
  const { t } = useI18n();
  const [rights, setRights] = useState({
    lic_ocr_enabled: !!user.lic_ocr_enabled,
    lic_projects_enabled: !!user.lic_projects_enabled,
    lic_annotations_enabled: !!user.lic_annotations_enabled,
    lic_ratings_enabled: !!user.lic_ratings_enabled,
  });
  // `''` couvre les deux cas « non rattaché » et « pas de plafond » ; ils sont
  // convertis en `null` à l'envoi, seule valeur que le serveur interprète
  // comme un détachement (une chaîne vide serait refusée).
  const [orgUnitId, setOrgUnitId] = useState(user.org_unit_id || '');
  const [quota, setQuota] = useState(user.lic_quota_max ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await onSave(user.id, {
      ...(cabinet ? {} : rights),
      org_unit_id: orgUnitId || null,
      lic_quota_max: quota === '' ? null : parseInt(quota, 10),
    });
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 12, padding: 24, width: 360, maxWidth: '90vw', boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{t('ent_edit_seat')}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>{user.email}</div>
        <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2 }}>{t('ent_rights_label')}</div>
          {cabinet ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('portal_cabinet_rights_included')}</div>
          ) : [
            ['lic_ocr_enabled', t('ent_rights_ocr')],
            ['lic_projects_enabled', t('ent_rights_projects')],
            ['lic_annotations_enabled', t('ent_rights_annotations')],
            ['lic_ratings_enabled', t('ent_rights_ratings')],
          ].map(([k, label]) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={rights[k]} onChange={e => setRights(r => ({ ...r, [k]: e.target.checked }))} />
              {label}
            </label>
          ))}
        </div>

        {/* Rattachement — tous niveaux confondus : un référent régional est
            rattaché à une région, pas à une agence. */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('portal_seat_org')}</label>
          <select value={orgUnitId} onChange={e => setOrgUnitId(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)' }}>
            <option value="">{t('portal_seat_org_none')}</option>
            {ordonnerArbre(orgUnits || []).map(u => (
              <option key={u.id} value={u.id}>
                {`${'— '.repeat(u.profondeur)}${u.nom} (${t(`org_level_${u.niveau}`)})`}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('portal_seat_quota')}</label>
          <input type="number" min="0" value={quota} onChange={e => setQuota(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{t('portal_seat_quota_hint')}</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>{t('cancel')}</button>
          <button onClick={save} disabled={saving}
            style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {saving ? '…' : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ⚠️ `OrgUnitModal` vivait ici. Déplacée le 28/07/2026 dans
// `components/OrganisationEchelons.js`, avec tout l'onglet Organisation : le
// même écran sert désormais à Evalens pour monter l'organisation d'un client à
// la livraison. Une copie locale aurait fini par diverger — sa logique de
// niveaux acceptables est la partie la plus subtile du formulaire.
export default function LicensePortal() {
  const { t } = useI18n();
  const { user, refreshUser } = useAuth();
  // Licence née d'un abonnement Cabinet : les droits des sièges sont ceux du
  // forfait, imposés par le serveur (`DROITS_SIEGE_CABINET`). Les cases ne sont
  // donc pas proposées — elles laisseraient croire à un réglage qui n'existe pas.
  const estCabinet = !!user?.license?.facturation_stripe;
  const [tab, setTab] = useState('seats');
  const [dashboard, setDashboard] = useState(null);
  // Chargé avant tout `return` anticipé : un hook ne s'appelle pas sous condition.
  const logoEnTete = useLogoLicence(dashboard?.license?.logo_path);
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsUserId, setStatsUserId] = useState('');
  const [statsOrgUnitId, setStatsOrgUnitId] = useState('');
  const [orgUnits, setOrgUnits] = useState([]);
  const [statsTypeRapport, setStatsTypeRapport] = useState('');
  const [statsSociete, setStatsSociete] = useState('');
  const [statsRuleFilter, setStatsRuleFilter] = useState('');
  // Période du bloc Consommations — même comportement que Mes statistiques,
  // mais portée sur toute la licence (ou le membre/échelon sélectionné).
  const [statsPeriode, setStatsPeriode] = useState(() => {
    const maintenant = new Date();
    return { granularite: 'mois', annee: maintenant.getFullYear(), mois: maintenant.getMonth() + 1 };
  });
  const [annotations, setAnnotations] = useState([]);
  const [ratings, setRatings] = useState([]);
  // Filtres de l'onglet Contenu — appliqués CÔTÉ SERVEUR : les deux routes
  // plafonnent à 200 lignes, filtrer après coup dans le navigateur ferait
  // passer une agence pour vide dès que la licence dépasse ce plafond.
  const [contentOrgUnitId, setContentOrgUnitId] = useState('');
  const [contentUserId, setContentUserId] = useState('');
  // Filtre de l'onglet Sièges (rattachement direct) — purement local, la liste
  // complète des sièges est déjà chargée.
  const [seatsOrgUnitId, setSeatsOrgUnitId] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [editSeat, setEditSeat] = useState(null);
  const [actionError, setActionError] = useState('');
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    const r = await fetch(`${API}/api/license/dashboard`, { headers: auth() });
    if (r.ok) setDashboard((await r.json()));
  }, []);

  const loadUsers = useCallback(async () => {
    const r = await fetch(`${API}/api/license/users`, { headers: auth() });
    if (r.ok) setUsers((await r.json()).users || []);
  }, []);

  const loadInvitations = useCallback(async () => {
    const r = await fetch(`${API}/api/license/invitations`, { headers: auth() });
    if (r.ok) setInvitations((await r.json()).invitations || []);
  }, []);

  const loadStats = useCallback(async () => {
    try {
      setStats(await getLicenseStats({
        userId: statsUserId || undefined,
        orgUnitId: statsOrgUnitId || undefined,
        typeRapport: statsTypeRapport || undefined,
        societe: statsSociete || undefined,
        ...statsPeriode,
      }));
    } catch (_) { /* ignore — stats tab a déjà sa propre gestion "pas de données" */ }
  }, [statsUserId, statsOrgUnitId, statsTypeRapport, statsSociete, statsPeriode]);

  const loadOrgUnits = useCallback(async () => {
    // Cabinet : pas d'échelon (réservés à l'Entreprise). Une liste vide masque
    // d'elle-même les filtres et le choix d'échelon à l'invitation.
    if (estCabinet) { setOrgUnits([]); return; }
    try {
      setOrgUnits((await getLicenseOrgUnits()).org_units || []);
    } catch (_) { /* un membre sans droit ne voit simplement pas d'échelon */ }
  }, [estCabinet]);

  const filtresContenu = useMemo(
    () => ({ userId: contentUserId || undefined, orgUnitId: contentOrgUnitId || undefined }),
    [contentUserId, contentOrgUnitId]
  );

  const loadAnnotations = useCallback(async () => {
    try { setAnnotations(await getLicenseAnnotations(filtresContenu)); }
    catch (_) { /* droit absent : l'onglet gère déjà son propre « aucune donnée » */ }
  }, [filtresContenu]);

  const loadRatings = useCallback(async () => {
    try { setRatings(await getLicenseRatings(filtresContenu)); }
    catch (_) { /* idem */ }
  }, [filtresContenu]);

  // Les échelons sont chargés d'emblée, pas seulement à l'ouverture de leur
  // onglet : le tableau des sièges et le filtre des statistiques en ont besoin.
  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadDashboard(), loadUsers(), loadInvitations(), loadOrgUnits()]);
      setLoading(false);
    })();
  }, [loadDashboard, loadUsers, loadInvitations, loadOrgUnits]);

  useEffect(() => {
    if (tab === 'stats' && !stats) loadStats();
    if (tab === 'content' && annotations.length === 0) { loadAnnotations(); loadRatings(); }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 'stats') loadStats();
  }, [statsUserId, statsOrgUnitId, statsTypeRapport, statsSociete, statsPeriode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Le filtre de l'onglet Contenu déclenche un vrai rechargement, pas un tri
  // local : c'est le serveur qui applique le filtre avant son plafond de 200.
  useEffect(() => {
    if (tab === 'content') { loadAnnotations(); loadRatings(); }
  }, [filtresContenu]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRevoke = async (userId) => {
    if (!window.confirm(t('portal_revoke_confirm'))) return;
    setActionError('');
    const r = await fetch(`${API}/api/license/users/${userId}/revoke`, { method: 'POST', headers: auth() });
    if (!r.ok) { const d = await r.json(); setActionError(d.error || t('err_generic')); return; }
    await loadUsers();
  };

  // Réactiver rend l'accès à l'application et reprend un siège sur le contingent
  // de la licence : aussi conséquent que de le retirer. La confirmation manquait
  // du seul côté « restaurer » (signalé par Marc le 29/07/2026).
  const handleRestore = async (userId) => {
    if (!window.confirm(t('portal_restore_confirm'))) return;
    setActionError('');
    const r = await fetch(`${API}/api/license/users/${userId}/restore`, { method: 'POST', headers: auth() });
    if (!r.ok) { const d = await r.json(); setActionError(d.error || t('err_generic')); return; }
    await loadUsers();
  };

  const handleUpdateSeat = async (userId, updates) => {
    setActionError('');
    const r = await fetch(`${API}/api/license/users/${userId}`, {
      method: 'PATCH',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (r.ok) { setEditSeat(null); await loadUsers(); }
    else { const d = await r.json(); setActionError(d.error || t('ent_err_update_seat')); }
  };

  const handleCancelInvite = async (invId) => {
    if (!window.confirm(t('ent_cancel_invite_confirm'))) return;
    const licId = user?.license?.id || dashboard?.license?.id;
    if (!licId) return;
    await fetch(`${API}/api/enterprise/licenses/${licId}/invitations/${invId}`, { method: 'DELETE', headers: auth() });
    await loadInvitations();
  };

  // Renvoi (15/09/2026) : nouveau lien, 7 jours. Le serveur refuse si renvoyer
  // une invitation expirée dépasserait le nombre de sièges.
  const handleResendInvite = async (invId) => {
    const licId = user?.license?.id || dashboard?.license?.id;
    if (!licId) return;
    setActionError('');
    const r = await fetch(`${API}/api/enterprise/licenses/${licId}/invitations/${invId}/resend`, { method: 'POST', headers: auth() });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setActionError(d.error || t('err_generic')); return; }
    window.alert(t('ent_inv_resent'));
    await Promise.all([loadInvitations(), loadDashboard()]);
  };

  const badgeStyle = (active) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
    background: active ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
    color: active ? '#059669' : '#ef4444',
  });

  // Le tableau trie sur la VALEUR de la ligne, pas sur ce que rend `render` :
  // sans libellé pré-calculé, trier « Rôle » classerait sur `license_admin` /
  // `member`, les codes internes, au lieu de l'affichage traduit.
  const seatsRows = useMemo(() => users
    .filter(u => !seatsOrgUnitId || u.org_unit_id === seatsOrgUnitId)
    .map(u => ({
      ...u,
      role_label: u.license_role === 'license_admin' ? t('ent_role_admin') : t('ent_role_member'),
      org_label: u.org_unit_nom || '',
    })),
    [users, seatsOrgUnitId, t]);

  // Même raison que `seatsRows` : les libellés traduits sont pré-calculés pour
  // que le tri porte sur ce qui est affiché, pas sur les codes internes
  // (`parsing`, `in_progress`…). La date est tronquée au jour, ce qui garde un
  // tri chronologique correct puisque le format ISO se trie lexicographiquement.
  const annotationsRows = useMemo(() => {
    const statutsAnnotation = {
      open: t('ann_status_open'), processing: t('ann_status_processing'),
      rejected: t('ann_status_rejected'), closed: t('ann_status_closed'),
    };
    const statutsResolution = {
      resolved: t('resolution_resolved'), unresolved: t('resolution_unresolved'),
      in_progress: t('resolution_in_progress'), unknown: t('resolution_unknown'),
    };
    const types = getAnnotationTypes(t);
    const cibles = getAnnotationTargets(t);
    return annotations.map(a => ({
      ...a,
      date: (a.created_at || '').slice(0, 10),
      org_label: a.org_unit_nom || '',
      type_label: types[a.type] || a.type,
      target_label: cibles[a.target] || a.target,
      statut_label: statutsAnnotation[a.annotation_status || 'open'],
      resolution_label: statutsResolution[a.resolution_status || 'unknown'],
    }));
  }, [annotations, t]);

  const ratingsRows = useMemo(() => ratings.map(r => ({
    ...r,
    date: (r.created_at || '').slice(0, 10),
    org_label: r.org_unit_nom || '',
  })), [ratings]);

  const lic = dashboard?.license;
  const licStats = dashboard?.stats;
  const licId = lic?.id || user?.license?.id;

  // Échelons et plafonds par membre : réservés à l'Entreprise (CGV 1.4, art. 3.1),
  // le serveur refuse de les créer sur un Cabinet (`_refus_echelons_cabinet`).
  const availableTabs = [
    ['seats', t('portal_tab_seats'), true],
    ['org', t('portal_tab_org'), !estCabinet],
    ['invitations', t('portal_tab_invitations'), true],
    ['stats', t('portal_tab_stats'), lic?.can_see_stats],
    ['content', t('portal_tab_content'), lic?.can_see_annotations || lic?.can_see_ratings],
  ].filter(([, , visible]) => visible);

  const poolPct = (licStats?.pool_used_this_month != null && lic?.pool_analyses_count)
    ? Math.round((licStats.pool_used_this_month / lic.pool_analyses_count) * 100)
    : null;
  // Au-delà du pool, même d'une seule analyse, l'alerte change de nature : le
  // dépassement n'est plus à anticiper, il est constaté. Elle donne alors le
  // NOMBRE d'analyses en plus et le prix unitaire — ni pourcentage, ni conseil
  // (décision du 15/09/2026). Le prix n'est rappelé que s'il est réellement
  // facturé : une licence en mode `block` n'en a pas.
  // Clés composées ci-dessous : portal_pool_depasse_singular / _plural,
  // portal_pool_depasse_prix_singular / _prix_plural.
  const poolDepasse = (poolPct !== null)
    ? Math.max(0, licStats.pool_used_this_month - lic.pool_analyses_count)
    : 0;
  const messageDepassement = (() => {
    if (poolDepasse === 0) return null;
    const pluriel = poolDepasse > 1 ? 'plural' : 'singular';
    // Toujours facturé depuis le 28/09/2026 : seul le prix connu décide du message.
    const avecPrix = lic?.pool_overage_price != null;
    return t(`portal_pool_depasse${avecPrix ? '_prix' : ''}_${pluriel}`)
      .replace('{n}', poolDepasse)
      .replace('{prix}', avecPrix ? Number(lic.pool_overage_price).toFixed(2).replace('.', ',') : '');
  })();

  if (loading) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>{t('loading_label')}</div>;

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
    {/* Même gabarit que « Mes analyses » et « Mes statistiques » : cette page
        porte désormais des tableaux à 9 colonnes, que 900 px comprimaient. */}
    <main className="container mx-auto px-4 py-8 max-w-6xl flex-1">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <LayoutDashboard size={22} style={{ color: 'var(--blue)' }} />
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{t('portal_title')}</div>
          {lic && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{lic.nom} · <code style={{ fontSize: 11, background: 'var(--gray)', padding: '1px 6px', borderRadius: 4 }}>{lic.code}</code></div>}
        </div>
        {logoEnTete && (
          <img src={logoEnTete} alt="Logo"
            style={{ maxHeight: 36, maxWidth: 120, objectFit: 'contain', marginLeft: 'auto' }} />
        )}
      </div>

      {lic?.est_titulaire_cabinet && (
        <IdentiteCabinet lic={lic} onChange={async () => { await loadDashboard(); await refreshUser(); }} />
      )}

      {actionError && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid #ef4444', borderRadius: 8, padding: '10px 14px', color: '#ef4444', fontSize: 13, marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          {actionError}<button onClick={() => setActionError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}><X size={14} /></button>
        </div>
      )}

      {/* KPIs */}
      {licStats && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          <StatCard label={t('portal_seats_used')} value={licStats.active_seats} sub={lic?.seats_max != null ? `/${lic.seats_max} max` : t('ent_seats_unlimited')} />
          <StatCard label={t('portal_pool_used')} value={licStats.pool_used_this_month} sub={lic?.pool_analyses_count != null ? `/${lic.pool_analyses_count} ${lic.pool_analyses_type === 'annual' ? t('ent_annual') : t('ent_monthly')}` : undefined} />
          {licStats.pending_invites > 0 && <StatCard label={t('portal_pending_invites')} value={licStats.pending_invites} />}
        </div>
      )}

      {/* Garde-fou du contrat atteint : les analyses sont suspendues (28/09/2026). */}
      {licStats?.garde_fou?.atteint && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid #ef4444', borderRadius: 8, padding: '10px 14px', color: '#b91c1c', fontSize: 13, marginBottom: 16 }}>
          {t('portal_garde_fou_atteint')}
        </div>
      )}

      {/* Alerte pool : dépassement constaté, sinon approche du seuil (>= 80 %) */}
      {poolPct !== null && poolPct >= 80 && (
        <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid #f59e0b', borderRadius: 8, padding: '10px 14px', color: '#92400e', fontSize: 13, marginBottom: 16 }}>
          {messageDepassement || `${t('portal_pool_alert_prefix')} ${poolPct} % ${t('portal_pool_alert_suffix')}`}
        </div>
      )}

      {/* Onglets */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--gray)', marginBottom: 20, gap: 0 }}>
        {availableTabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ padding: '8px 18px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: tab === k ? 700 : 400, color: tab === k ? 'var(--blue)' : 'var(--black)', borderBottom: `2px solid ${tab === k ? 'var(--blue)' : 'transparent'}`, marginBottom: -1 }}>
            {label}
          </button>
        ))}
      </div>

      {/* Sièges */}
      {tab === 'seats' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {/* Rattachement DIRECT : une région n'affiche pas les sièges de ses
                agences. C'est la question posée ici — « qui est rattaché à cet
                échelon », pas « qui travaille sous cet échelon ». */}
            <FiltresOrgMembre
              orgUnits={orgUnits} users={users} t={t} avecMembre={false}
              orgUnitId={seatsOrgUnitId} userId=""
              onChange={({ orgUnitId }) => setSeatsOrgUnitId(orgUnitId)}
            />
            <button onClick={() => setShowInvite(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              <MailPlus size={14} />{t('portal_invite_btn')}
            </button>
          </div>
          <SortableFilterableTable
            hideSearch
            rows={seatsRows}
            emptyLabel={t('portal_no_seats')}
            columns={[
              { key: 'email', label: t('field_email') },
              { key: 'role_label', label: t('ent_role_label') },
              ...(estCabinet ? [] : [{
                key: 'org_label', label: t('portal_seat_org'),
                render: u => (
                  <>
                    {u.org_unit_nom
                      ? <span>{u.org_unit_nom}</span>
                      : <span style={{ color: 'var(--text-muted)' }}>{t('portal_seat_org_none')}</span>}
                    {u.lic_quota_max != null && (
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                        {t('portal_seat_quota')} : {u.lic_quota_max}
                      </div>
                    )}
                  </>
                ),
              }]),
              { key: 'lic_ocr_enabled', label: t('ent_rights_ocr'), align: 'center', render: u => (u.lic_ocr_enabled ? '✓' : '—') },
              { key: 'lic_projects_enabled', label: t('ent_rights_projects'), align: 'center', render: u => (u.lic_projects_enabled ? '✓' : '—') },
              { key: 'lic_annotations_enabled', label: t('ent_rights_annotations'), align: 'center', render: u => (u.lic_annotations_enabled ? '✓' : '—') },
              { key: 'lic_ratings_enabled', label: t('ent_rights_ratings'), align: 'center', render: u => (u.lic_ratings_enabled ? '✓' : '—') },
              {
                key: 'is_active', label: t('ent_status'),
                render: u => (
                  <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                    <span style={badgeStyle(u.is_active)}>{u.is_active ? t('ent_seats_active') : t('ent_seats_revoked')}</span>
                    {/* Booléen calculé par le serveur, sans date : aide à libérer
                        un siège, pas suivi d'activité (15/09/2026). */}
                    {u.inactif && (
                      <span title={t('portal_siege_inactif_aide')}
                        style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: 'rgba(245,158,11,0.12)', color: '#92400e' }}>
                        {t('portal_siege_inactif')}
                      </span>
                    )}
                  </span>
                ),
              },
              {
                key: 'actions', label: '', sortable: false,
                render: u => (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {/* Cabinet : droits fixés par le forfait, ni échelon ni plafond —
                        la fenêtre n'aurait plus rien à modifier. */}
                    {!estCabinet && (
                      <button onClick={() => setEditSeat(u)}
                        style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 11 }}>
                        <Pencil size={10} />{t('ent_edit')}
                      </button>
                    )}
                    {u.id !== user?.id && (
                      u.is_active ? (
                        <button onClick={() => handleRevoke(u.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px', borderRadius: 4, border: '1px solid #ef4444', background: 'transparent', cursor: 'pointer', fontSize: 11, color: '#ef4444' }}>
                          <ShieldOff size={11} />{t('ent_revoke_user')}
                        </button>
                      ) : (
                        <button onClick={() => handleRestore(u.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px', borderRadius: 4, border: '1px solid #059669', background: 'transparent', cursor: 'pointer', fontSize: 11, color: '#059669' }}>
                          <ShieldCheck size={11} />{t('ent_restore_user')}
                        </button>
                      )
                    )}
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}

      {/* Organisation — échelons et sous-quotas */}
      {/* Organisation — échelons et sous-quotas. Le composant charge sa propre
          liste ; `onChanged` fait recharger celle de cette page, dont dépendent
          les filtres et le formulaire de rattachement d'un siège. */}
      {tab === 'org' && <OrganisationEchelons onChanged={loadOrgUnits} />}

      {/* Invitations */}
      {tab === 'invitations' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button onClick={() => setShowInvite(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              <MailPlus size={14} />{t('portal_invite_btn')}
            </button>
          </div>
          {invitations.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '20px 0' }}>{t('portal_no_invites')}</div>}
          {invitations.map(inv => (
            <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--gray)', marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{inv.email}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {inv.accepted_at
                    ? `${t('ent_inv_accepted_on')} ${inv.accepted_at.slice(0, 10)}`
                    // Dates et non chaînes : `expires_at` est une heure locale
                    // sans fuseau, `toISOString()` serait en UTC.
                    : inv.expires_at && new Date(inv.expires_at) <= new Date()
                      ? `${t('ent_inv_expired_on')} ${(inv.expires_at || '').slice(0, 10)}`
                      : `${t('ent_inv_pending')} — ${t('ent_inv_expires_on')} ${(inv.expires_at || '').slice(0, 10)}`}
                </div>
              </div>
              {!inv.accepted_at && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => handleResendInvite(inv.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 12 }}>
                    <MailPlus size={12} />{t('ent_inv_resend')}
                  </button>
                  <button onClick={() => handleCancelInvite(inv.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 12 }}>
                    <X size={12} />{t('ent_inv_cancel')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Statistiques */}
      {tab === 'stats' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {/* En tête : l'échelon seul, qui vaut pour toute la page. Le filtre
                MEMBRE n'agit que sur les Consommations, et il est rangé dans leur
                bloc : les résultats par règle ne se filtrent jamais par personne
                (15/09/2026, cf `/api/license/stats`). L'échelon restreint
                toujours la liste des membres proposés. */}
            <FiltresOrgMembre
              orgUnits={orgUnits} users={users} t={t} avecMembre={false}
              orgUnitId={statsOrgUnitId} userId={statsUserId}
              onChange={({ orgUnitId, userId }) => { setStatsOrgUnitId(orgUnitId); setStatsUserId(userId); }}
            />
            <select value={statsTypeRapport} onChange={e => setStatsTypeRapport(e.target.value)} style={selectStyle}>
              <option value="">{t('stat_all_type_rapports')}</option>
              {(stats?.resultats?.available_type_rapports || []).map(tr => (
                <option key={tr} value={tr}>{t(`type_rapport_${tr}`) || tr}</option>
              ))}
            </select>
            {/* Sociétés des rapports analysés PAR CETTE LICENCE seulement
                (`available_societes`, borné au périmètre depuis a7ca224). */}
            <select value={statsSociete} onChange={e => setStatsSociete(e.target.value)} style={{ ...selectStyle, maxWidth: 220 }}>
              <option value="">{t('stat_all_societes')}</option>
              {(stats?.resultats?.available_societes || []).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {!stats && <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '20px 0' }}>{t('portal_no_stats')}</div>}
          {stats && (() => {
            const resultatsRowsAll = stats.resultats?.par_regle || [];
            const resultatsRows = statsRuleFilter
              ? resultatsRowsAll.filter(r => String(r.rule_number) === statsRuleFilter)
              : resultatsRowsAll;
            return (
              <>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <StatCard label={t('portal_stats_total')} value={stats.total} />
                  <StatCard label={t('portal_stats_period')} value={stats.this_month} />
                </div>

                <ConsommationsClient
                  data={stats.consommations}
                  granularite={statsPeriode.granularite}
                  annee={statsPeriode.annee}
                  mois={statsPeriode.mois}
                  onChange={(modif) => setStatsPeriode(p => ({ ...p, ...modif }))}
                  actions={
                    <>
                      <select value={statsUserId} onChange={e => setStatsUserId(e.target.value)}
                        title={t('portal_filtre_membre_consommations')} style={selectStyle}>
                        <option value="">{t('portal_stats_all_members')}</option>
                        {users.filter(u => !statsOrgUnitId || u.org_unit_id === statsOrgUnitId)
                          .map(u => <option key={u.id} value={u.id}>{u.email}</option>)}
                      </select>
                      <button onClick={() => exportLicenseConsommations({
                          userId: statsUserId || undefined,
                          orgUnitId: statsOrgUnitId || undefined,
                          ...statsPeriode,
                        }).catch(e => alert(e.message))}
                        style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                        {t('export_excel')}
                      </button>
                    </>
                  }
                />

                <div style={{ background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--gray)', padding: '20px 24px', marginTop: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                    <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--black)', margin: 0 }}>{t('stat_resultats_title')}</h2>
                    <select value={statsRuleFilter} onChange={e => setStatsRuleFilter(e.target.value)} style={selectStyle}>
                      <option value="">{t('stat_all_rules')}</option>
                      {resultatsRowsAll.map(r => (
                        <option key={r.rule_number} value={r.rule_number}>{`R${r.rule_number} — ${r.rule_name}`}</option>
                      ))}
                    </select>
                    <button onClick={() => exportLicenseResultatsAnalyses({ orgUnitId: statsOrgUnitId || undefined, typeRapport: statsTypeRapport || undefined, societe: statsSociete || undefined }).catch(e => alert(e.message))}
                      style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                      {t('export_excel')}
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
                    <div>
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('stat_nb_rapports')}</p>
                      <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--black)', margin: 0 }}>{stats.resultats?.nb_rapports ?? 0}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('stat_avg_favorable')}</p>
                      <p style={{ fontSize: 20, fontWeight: 700, color: '#22c55e', margin: 0 }}>{stats.resultats?.avg_favorable ?? '—'}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('stat_avg_reserve')}</p>
                      <p style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b', margin: 0 }}>{stats.resultats?.avg_reserve ?? '—'}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('stat_avg_defavorable')}</p>
                      <p style={{ fontSize: 20, fontWeight: 700, color: '#ef4444', margin: 0 }}>{stats.resultats?.avg_defavorable ?? '—'}</p>
                    </div>
                  </div>
                  <SortableFilterableTable
                    hideSearch
                    columns={[
                      { key: 'rule_number', label: t('stat_col_rule_number'), align: 'right' },
                      { key: 'rule_name', label: t('stat_col_rule_name') },
                      { key: 'total', label: t('stat_col_total_avis'), align: 'right' },
                      { key: 'taux_favorable', label: t('stat_col_pct_favorable'), align: 'right', render: r => r.taux_favorable != null ? `${r.taux_favorable}%` : '—' },
                      { key: 'taux_reserve', label: t('stat_col_pct_reserve'), align: 'right', render: r => r.taux_reserve != null ? `${r.taux_reserve}%` : '—' },
                      { key: 'taux_defavorable', label: t('stat_col_pct_defavorable'), align: 'right', render: r => r.taux_defavorable != null ? `${r.taux_defavorable}%` : '—' },
                    ]}
                    rows={resultatsRows}
                    emptyLabel={t('stat_resultats_empty')}
                  />
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Annotations & Évaluations */}
      {tab === 'content' && (
        <div>
          {/* Ces deux filtres provoquent un RECHARGEMENT : le serveur plafonne à
              200 lignes et applique le filtre avant ce plafond. Filtrer ici,
              dans le navigateur, ferait passer une agence pour vide dès que la
              licence dépasse 200 annotations au total. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <FiltresOrgMembre
              orgUnits={orgUnits} users={users} t={t}
              orgUnitId={contentOrgUnitId} userId={contentUserId}
              onChange={({ orgUnitId, userId }) => { setContentOrgUnitId(orgUnitId); setContentUserId(userId); }}
            />
          </div>

          {lic?.can_see_annotations && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{t('ent_rights_annotations')}</div>
              <SortableFilterableTable
                rows={annotationsRows}
                emptyLabel={t('ent_no_annotations')}
                searchPlaceholder={t('portal_search_placeholder')}
                searchKeys={['email', 'org_label', 'filename', 'content']}
                columns={[
                  { key: 'date', label: t('col_date') },
                  { key: 'email', label: t('field_email') },
                  { key: 'org_label', label: t('portal_seat_org'), render: a => a.org_label || <span style={{ color: 'var(--text-muted)' }}>{t('portal_seat_org_none')}</span> },
                  { key: 'filename', label: t('col_analysis') },
                  {
                    key: 'type_label', label: t('ann_type_label'),
                    render: a => {
                      const c = TYPE_COLORS[a.type] || TYPE_COLORS.other;
                      return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: c.bg, color: c.text }}>{a.type_label}</span>;
                    },
                  },
                  { key: 'target_label', label: t('ann_target_label') },
                  {
                    key: 'statut_label', label: t('ann_status_label'),
                    render: a => {
                      const c = ANN_STATUS_COLORS[a.annotation_status || 'open'];
                      return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: c.bg, color: c.text }}>{a.statut_label}</span>;
                    },
                  },
                  {
                    key: 'resolution_label', label: t('resolution_label'),
                    render: a => {
                      const c = RES_STATUS_COLORS[a.resolution_status || 'unknown'];
                      return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: c.bg, color: c.text }}>{a.resolution_label}</span>;
                    },
                  },
                  { key: 'content', label: t('col_content'), sortable: false, render: a => <div style={{ minWidth: 220, maxWidth: 420 }}>{a.content}</div> },
                ]}
              />
            </div>
          )}

          {lic?.can_see_ratings && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{t('ent_rights_ratings')}</div>
              <SortableFilterableTable
                rows={ratingsRows}
                emptyLabel={t('ent_no_ratings')}
                searchPlaceholder={t('portal_search_placeholder')}
                searchKeys={['email', 'org_label', 'comment']}
                columns={[
                  { key: 'date', label: t('col_date') },
                  { key: 'email', label: t('field_email') },
                  { key: 'org_label', label: t('portal_seat_org'), render: r => r.org_label || <span style={{ color: 'var(--text-muted)' }}>{t('portal_seat_org_none')}</span> },
                  { key: 'score', label: t('col_note'), align: 'right', render: r => <span style={{ fontWeight: 700, color: 'var(--blue)' }}>{r.score}/5</span> },
                  { key: 'comment', label: t('col_comment'), sortable: false, render: r => <div style={{ minWidth: 220, maxWidth: 480 }}>{r.comment || '—'}</div> },
                ]}
              />
            </div>
          )}
        </div>
      )}

      {showInvite && licId && (
        <InviteModal licenseId={licId} orgUnits={orgUnits} cabinet={estCabinet} onClose={() => setShowInvite(false)}
          onSent={() => { loadInvitations(); loadDashboard(); }} />
      )}

      {editSeat && (
        <EditSeatModal user={editSeat} orgUnits={orgUnits} cabinet={estCabinet} onSave={handleUpdateSeat} onClose={() => setEditSeat(null)} />
      )}
    </main>
    </div>
  );
}
